import BaseModel, { type IResourceName } from 'volta/models/base-model';
import { _normalize, collectionCommand } from 'volta/utils/api';

import { attr } from '@ember-data/model';

import type {
  IAppFeature,
  IFeatureExecution,
  IFeatureExperimental,
  IFeatureExport,
  IFeaturePlanning,
  IFeaturePOWorkbench,
  IFeatureSimulation,
  IFeatureSku,
  IFeatureWorkshop,
  IForecastFeature,
  THiddenFields
} from './types/app-features';

import { Type } from '@warp-drive/core-types/symbols';

export const HIDDEN_FIELDS: THiddenFields[] = [
  'part',
  'unitMeasure',
  'partType',
  'acqCode',
  'tags',
  'customFields',
  'leadTime',
  'internalLeadTime',
  'minOrderQty',
  'orderMultiple',
  'maxPOQty',
  'warehouse',
  'supplier',
  'workshop',
  'deliveryWorkshop',
  'orderType',
  'inventoryUnitPrice',
  'salesUnitPrice',
  'leadTimeCategory',
  'varCategory',
  'abcClass',
  'xyzClass'
];

export const SKU_KEYS = [
  'part_code',
  'part_description',
  'warehouse_id',
  'warehouse_code',
  'warehouse_description',
  'part_unit_measure',
  'acq_code',
  'lead_time',
  'volta_lead_time',
  'min_order_qty',
  'order_multiple',
  'inventory_unit_price',
  'sales_unit_price',
  'planner',
  'buyer',
  'internal_lead_time',
  'description',
  'max_po_qty',
  'order_type'
];

export const DEFAULT_SKU_STATS = ['criticalCount'];

export type TItemStat =
  | 'title'
  | 'availableOnHand'
  | 'unavailableOnHand'
  | 'minThreshold'
  | 'capacity'
  | 'gauge';

export const StockBlockItemStats: TItemStat[] = [
  'title',
  'availableOnHand',
  'unavailableOnHand',
  'minThreshold',
  'capacity',
  'gauge'
];

export const AppFeatureResourceName: IResourceName = {
  singular: 'appFeature',
  plural: 'appFeatures'
};

export const AppFeatureEvents = {
  AppFeatureUpdated: 'AppFeatureUpdated'
};

enum TAppFeatureCommands {
  UpdateAppFeature = 'UpdateAppFeature'
}

export default class AppFeature extends BaseModel implements IAppFeature {
  static updateAppFeature = collectionCommand<IAppFeature, AppFeature, typeof AppFeature>(
    TAppFeatureCommands.UpdateAppFeature
  );

  @attr()
  experimental!: IFeatureExperimental;

  @attr()
  export!: IFeatureExport;

  @attr()
  poWorkbench!: IFeaturePOWorkbench;

  @attr()
  planning!: IFeaturePlanning;

  @attr()
  execution!: IFeatureExecution;

  @attr()
  simulation!: IFeatureSimulation;

  @attr()
  sku!: IFeatureSku;

  @attr()
  workshop!: IFeatureWorkshop;

  @attr({ defaultValue: () => ({ projectsEnabled: false }) })
  forecast!: IForecastFeature;

  [Type] = 'app-feature' as const;
}
